import { FCC, TOption } from "@/types/common";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { Control, useFieldArray, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useGetTalentSchema } from "@/features/talent/hooks/useGetTalentSchema";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { dayjs } from "@/utils/dayjs";
import { YEAR_MONTH_STR_FORMAT } from "@/constants/datetime";
import { RadioGroup } from "@/components/elements/RadioGroup";
import { useGetLanguageTestList, useGetMajorList, useGetNationalityList, useGetSpecificSkillCertificateList } from "@/api/services/global/master";
import { useEnum } from "@/api/services/global/enum";
import { Form } from "@/components/elements/Form";
import { CityInput } from "@/components/elements/CityInput";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import { ERecruitmentStatus, TALENT_LOCATION, TALENT_LOCATION_OPTIONS } from "@/types/enum";
import { TalentIndustryFields } from "@/features/talent/components/talent-form/TalentIndustryFields";
import { SelectMulti } from "@/components/elements/SelectMulti";
import { TalentFormEducationFields } from "@/features/talent/components/talent-form/TalentFormEducationFields";
import { TalentFormHeldQualificationsFields } from "@/features/talent/components/talent-form/TalentFormHeldQualificationsFields";
import { TalentFormLanguageSkillsFields } from "@/features/talent/components/talent-form/TalentFormLanguageSkillsFields";
import { TalentFormResidenceStatus } from "@/features/talent/components/talent-form/TalentFormResidenceStatus";
import { TalentFormMemo } from "@/features/talent/components/talent-form/TalentFormMemo";
import { TalentFormContactMethods } from "@/features/talent/components/talent-form/TalentFormContactMethods";
import { InnerBox } from "@/components/elements/InnerBox";
import { black, blue, divider } from "@/theme/colors";
import ClearIcon from "@mui/icons-material/Clear";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Textarea } from "@/components/elements/Textarea";
import { useMe } from "@/api/services/main/auth";
import { sortNationalityOptions } from "@/features/talent/utils/sortNationalityOptions";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Sortable work-experience item — mirrors the one in CreateTalentFromResumeForm.
// Kept local to avoid coupling the two scan forms; logic is identical.
type SortableWorkExperienceItemProps = {
  fieldId: string;
  index: number;
  totalItems: number;
  control: Control<TTalentForm>;
  nationalityOptions: { label: string; value: string | number }[];
  onRemove: (index: number) => void;
  onAdd: (index: number) => void;
  canAdd: boolean;
};

const SortableWorkExperienceItem = ({
  fieldId,
  index,
  totalItems,
  control,
  nationalityOptions,
  onRemove,
  onAdd,
  canAdd,
}: SortableWorkExperienceItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: fieldId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <InnerBox mt={3} p={2} pt={0} showDivider={false} borderColor={divider.high} bordered={true}>
        {totalItems > 1 && (
          <Stack direction="row" alignItems="center" mt={2} mb={1} spacing={0.5}>
            <Box {...attributes} {...listeners} sx={{ cursor: "grab", display: "flex", alignItems: "center" }}>
              <DragIndicatorIcon sx={{ color: black[80], fontSize: 24 }} />
            </Box>
            <Typography variant="body14Bold" color={black[30]}>
              職歴{index + 1}
            </Typography>
          </Stack>
        )}

        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormField control={control} name={`workExperiences.${index}.jobType`} label="職種" currentValueType="text">
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
          </Grid>
        </Stack>

        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormField control={control} name={`workExperiences.${index}.companyName`} label="企業名" currentValueType="text">
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField
                control={control}
                name={`workExperiences.${index}.countryId`}
                label="国名"
                currentValueType="select"
                currentOptionsKey="nationality"
              >
                <Select options={nationalityOptions} />
              </FormField>
            </Grid>
          </Grid>
        </Stack>

        <Stack mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormField control={control} name={`workExperiences.${index}.startDate`} label="入社年月" currentValueType="yearMonth">
                <InputDatePicker
                  minDate={dayjs("1900-01-01")}
                  maxDate={dayjs()}
                  views={["year", "month"]}
                  openTo="month"
                  format={YEAR_MONTH_STR_FORMAT}
                />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name={`workExperiences.${index}.endDate`} label="退職年月" currentValueType="yearMonth">
                <InputDatePicker minDate={dayjs("1900-01-01")} views={["year", "month"]} openTo="month" format={YEAR_MONTH_STR_FORMAT} />
              </FormField>
            </Grid>
          </Grid>
        </Stack>
      </InnerBox>

      <Stack direction="row" alignItems="center" spacing={3} mt={1}>
        {canAdd && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer" }} onClick={() => onAdd(index)}>
            <AddCircleOutlineIcon sx={{ color: blue[80], fontSize: 18 }} />
            <Typography variant="cap12">職歴を追加</Typography>
          </Stack>
        )}
        {totalItems > 1 && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer" }} onClick={() => onRemove(index)}>
            <ClearIcon sx={{ color: black[80], fontSize: 18, display: "flex" }} />
            <Typography variant="cap12">削除</Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export type TCreateTalentFromAllForm = {
  defaultValues?: TTalentForm | null;
  onChange: (data: TTalentForm) => void;
  memo?: string | null;
  onValidate: (triggerFn: () => Promise<boolean>) => void;
};

/**
 * Form for the 全項目 tab of the unified scan modal (IROHANA_MATCH_DEV-280).
 *
 * Renders all 10 sections per the design mockup, in order:
 *   1. 経歴の基本情報   — basic info (name, birthday, gender, contacts, etc.)
 *   2. 出身地（国内）    — domestic origin address
 *   3. 本国における居住地 — home-country residence
 *   4. 在留資格情報      — residence-card / SSW1 fields (delegated to TalentFormResidenceStatus)
 *   5. 分野・日本語能力   — industry & JP language ability
 *   6. 学歴・保有資格     — education / qualifications / non-JP language
 *   7. 職歴              — work experience (sortable list)
 *   8. 特性メモ          — characteristic memo (delegated to TalentFormMemo)
 *   9. 面談評価（メモより）— interview evaluation (prefilled from upload-step memo)
 *  10. 家族構成          — family composition
 *
 * State logic mirrors CreateTalentFromResumeForm (same talent schema, same merged
 * form payload). Differences vs the resume form: includes 在留資格情報 (residence
 * card data) and 特性メモ — both come from the unified scan response.
 */
export const CreateTalentFromAllForm: FCC<TCreateTalentFromAllForm> = ({ defaultValues, onChange, onValidate, memo }) => {
  const { talentSchema } = useGetTalentSchema();
  const { nationalityOptions } = useGetNationalityList();
  const { data: genderOptions } = useEnum("EGender");
  const { languageTestLevelOptions } = useGetLanguageTestList();
  const { getCertificatesByIndustryId, specificSkillCertificateOptions } = useGetSpecificSkillCertificateList();
  const { majorOptions, getJobTypeListOptionByMajor } = useGetMajorList();
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const { data: user } = useMe();
  const [isReadOnly, setIsReadOnly] = useState(false);
  const validationContextRef = useRef({ isFormSubmitted: false });
  const isAddingRef = useRef(false);

  const methods = useForm<TTalentForm>({
    defaultValues: {
      ...defaultValues,
      workExperiences: defaultValues?.workExperiences?.length
        ? defaultValues.workExperiences
        : [
            {
              jobType: "",
              companyName: "",
              countryId: null,
              startDate: null,
              endDate: null,
            },
          ],
      // contactMethods is not extracted by the scan AI, so the response payload
      // never includes it. Seed one empty row so the field array renders in
      // the 全項目 tab; user can add more (up to 4) or fill / leave blank.
      contactMethods: defaultValues?.contactMethods?.length ? defaultValues.contactMethods : [{ contactType: null, contactInfo: null }],
    },
    resolver: yupResolver(talentSchema),
    context: validationContextRef.current,
    mode: "all",
    reValidateMode: "onBlur",
  });
  const {
    control,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors, isSubmitted, touchedFields },
  } = methods;

  const talentNationalityId = useWatch({ control, name: "nationalityId" });
  const nickName = useWatch({ control, name: "nickName" });
  const sortedNationalityOptions = useMemo(
    () => sortNationalityOptions(nationalityOptions, talentNationalityId),
    [nationalityOptions, talentNationalityId],
  );

  const { fields, replace } = useFieldArray({ control, name: "workExperiences" });
  const workExperiences = useWatch({ control, name: "workExperiences" }) || [];
  const companySpecificIndustryId = useWatch({ control, name: "companySpecificIndustryId" });
  const specificSkillCertificates = useWatch({ control, name: "specificSkillCertificates" });
  const majorId = useWatch({ control, name: "majorId" });

  const certificateOptions = useMemo(() => {
    let options: TOption[] = [];
    if (companySpecificIndustryId) {
      options = getCertificatesByIndustryId(companySpecificIndustryId);
    } else if (specificSkillCertificates && Array.isArray(specificSkillCertificates) && specificSkillCertificates.length > 0) {
      options = specificSkillCertificateOptions;
    }
    return options.map((option) => ({ ...option, value: Number(option.value) }));
  }, [companySpecificIndustryId, specificSkillCertificates, getCertificatesByIndustryId, specificSkillCertificateOptions]);

  const newWorkExperience = { jobType: "", companyName: "", countryId: null, startDate: null, endDate: null };

  const handleInsertField = (afterIndex: number) => {
    if (fields.length < 10) {
      isAddingRef.current = true;
      const currentValues = getValues("workExperiences") || [];
      const updated = [...currentValues];
      updated.splice(afterIndex + 1, 0, newWorkExperience);
      replace(updated);
      setRenderKey((k) => k + 1);
    }
  };

  const handleRemoveField = (index: number) => {
    if (fields.length > 1) {
      const experience = workExperiences[index];
      if (experience && !experience.jobType?.trim() && !experience.companyName?.trim() && !experience.startDate && !experience.endDate) {
        const currentValues = getValues("workExperiences") || [];
        const updated = currentValues.filter((_: unknown, i: number) => i !== index);
        replace(updated);
        setRenderKey((k) => k + 1);
      } else {
        setDeleteIndex(index);
        setIsDeleteModalOpen(true);
      }
    }
  };

  const locationInfo = useLocationInfo({
    methods,
    names: { zipCode: "zipCode", province: "prefecture", municipality: "city", address: "address" },
  });

  // Initial wiring: draft status, owner, prefill interview eval from upload-step memo.
  useEffect(() => {
    setValue("recruitmentStatus", ERecruitmentStatus.DRAFT);
    setValue("personInChargeId", Number(user?.data.id));
    setValue("companySpecificIndustryId", defaultValues?.companySpecificIndustryId);
    setValue("interviewEvaluation", memo);
    // Render blankPeriodMonthly=0 as empty (UI convention). See same comment
    // in CreateTalentFromCardTabForm for rationale.
    if (Number(getValues("blankPeriodMonthly")) === 0) {
      setValue("blankPeriodMonthly", null);
    }
  }, []);

  // Expose validate trigger to parent (used by save flow in useScanImageState).
  useEffect(() => {
    onValidate(async () => {
      validationContextRef.current.isFormSubmitted = true;
      let isValid = false;
      await methods.handleSubmit(
        () => {
          isValid = true;
        },
        () => {
          isValid = false;
        },
      )();
      return isValid;
    });
  }, [methods, errors, onValidate]);

  // Re-validate workExperiences after array mutations (skip the mutation that just added a row).
  useEffect(() => {
    if (isAddingRef.current) {
      isAddingRef.current = false;
      return;
    }
    if (isSubmitted) {
      trigger("workExperiences");
    }
  }, [workExperiences, trigger, isSubmitted]);

  // Re-validate fullName when nickName changes (draft allows fullName to be empty when nickName is provided).
  useEffect(() => {
    if (isSubmitted || touchedFields.fullName || touchedFields.nickName) {
      trigger("fullName");
    }
  }, [nickName, trigger, isSubmitted, touchedFields.fullName, touchedFields.nickName]);

  // Mirror form state to parent + lock address fields when location is overseas.
  useEffect(() => {
    const subscription = watch((value) => {
      onChange(getValues());
      setIsReadOnly(value.location === TALENT_LOCATION.OVERSEA);
    });
    const locationWatch = watch("location");
    setIsReadOnly(locationWatch === TALENT_LOCATION.OVERSEA);
    return () => subscription.unsubscribe();
  }, [watch, onChange, getValues]);

  const confirmDelete = () => {
    if (deleteIndex !== null && fields.length > 1) {
      const currentValues = getValues("workExperiences") || [];
      const updated = currentValues.filter((_: unknown, i: number) => i !== deleteIndex);
      replace(updated);
      setRenderKey((k) => k + 1);
    }
    setIsDeleteModalOpen(false);
    setDeleteIndex(null);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      const currentValues = getValues("workExperiences") || [];
      const reordered = [...currentValues];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);
      replace(reordered);
      setRenderKey((k) => k + 1);
    }
  };

  const fieldIds = fields.map((f) => f.id);
  const onSubmit = () => {};

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={talentSchema} shouldBlock={false} enableBlocker={false}>
      {/* 1. 経歴の基本情報 */}
      <Typography variant="sub16Bold">経歴の基本情報</Typography>
      <Grid container spacing={3} mt={0}>
        <Grid item xs={6}>
          <FormField control={control} name="fullName" required={!nickName?.trim()} sx={{ marginBottom: "24px" }} currentValueType="text">
            <Input />
          </FormField>
          <FormField control={control} name="nickName" sx={{ marginBottom: "24px" }} currentValueType="text">
            <Input placeholder="入力してください" />
          </FormField>
          <FormField control={control} name="nationalityId" currentValueType="select" currentOptionsKey="nationality">
            <Select placeholder="選択してください" options={nationalityOptions} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="birthday" sx={{ marginBottom: "24px" }} currentValueType="date">
            <InputDatePicker minDate={dayjs("1870/01/01")} maxDate={dayjs().subtract(18, "year")} />
          </FormField>
          <FormField control={control} name="talentNumber" sx={{ marginBottom: "24px" }} currentValueType="text">
            <Input placeholder="入力してください" />
          </FormField>
          <FormField control={control} name="gender" currentValueType="select" currentOptionsKey="gender">
            <RadioGroup direction="row" options={genderOptions} />
          </FormField>
        </Grid>
      </Grid>
      <Grid container spacing={3} mt={0}>
        <Grid item xs={6}>
          <FormField control={control} name="phoneNumber" currentValueType="text">
            <Input placeholder="入力してください" />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="email" currentValueType="text">
            <Input placeholder="入力してください" />
          </FormField>
        </Grid>
      </Grid>
      <TalentFormContactMethods />
      <Grid container spacing={3} mb={3} mt={0}>
        <Grid item xs={6}>
          <FormField control={control} name="location" currentValueType="select" currentOptionsKey="location">
            <Select placeholder="選択してください" options={TALENT_LOCATION_OPTIONS} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="religion" currentValueType="text">
            <Input placeholder="入力してください" />
          </FormField>
        </Grid>
      </Grid>

      {/* 2. 出身地（国内）— mockup uses 出身地 here (resume tab uses 現住所 wording). */}
      <Typography variant="sub16Bold">出身地（国内）</Typography>
      <Grid container spacing={3} mb={3} mt={0}>
        <Grid item xs={12}>
          <FormField control={control} name="zipCode" currentValueType="text">
            <Input placeholder="入力してください" disabled={isReadOnly} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="prefecture" currentValueType="text">
            <Input placeholder="入力してください" readonly />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <CityInput name="city" control={control} zipCodeFieldName="zipCode" locationInfo={locationInfo} disabled={isReadOnly} />
        </Grid>
        <Grid item xs={12}>
          <FormField control={control} name="address" currentValueType="text">
            <Input placeholder="入力してください" disabled={isReadOnly} />
          </FormField>
        </Grid>
      </Grid>

      {/* 3. 本国における居住地 */}
      <Typography variant="sub16Bold">本国における居住地</Typography>
      <Grid container spacing={3} mb={3} mt={0}>
        <Grid item xs={12}>
          <FormField control={control} name="country" currentValueType="text">
            <Input placeholder="入力してください" autoComplete="off" />
          </FormField>
        </Grid>
      </Grid>

      {/* 4. 在留資格情報 — drop-in component (residence card data + SSW1 transition period). */}
      <TalentFormResidenceStatus bordered={false} />

      {/* 5. 分野・日本語能力 */}
      <Stack mt={3} mb={3}>
        <Typography variant="sub16Bold">分野・日本語能力</Typography>
        <TalentIndustryFields />
        <Grid container spacing={3} mt={0}>
          <Grid item xs={6}>
            <FormField control={control} name="languageTestLevelIds" currentValueType="selectMulti" currentOptionsKey="languageTestLevel">
              <SelectMulti placeholder="選択してください" options={languageTestLevelOptions} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="languageTestOther" currentValueType="text">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </Stack>
      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormField control={control} name="specificSkillCertificates" currentValueType="selectMulti" currentOptionsKey="certificate">
              <SelectMulti placeholder="選択してください" options={certificateOptions} separator="、" />
            </FormField>
          </Grid>
        </Grid>
      </Stack>
      <Stack mt={2} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="majorId" currentValueType="select" currentOptionsKey="major">
              <Select placeholder="選択してください" options={majorOptions} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="jobTypeId" currentValueType="select" currentOptionsKey="jobType">
              <Select placeholder="選択してください" options={getJobTypeListOptionByMajor(Number(majorId))} />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      {/* 6. 学歴・保有資格 */}
      <Typography variant="sub16Bold">学歴・保有資格</Typography>
      <TalentFormEducationFields />
      <TalentFormHeldQualificationsFields />
      <TalentFormLanguageSkillsFields />

      {/* 7. 職歴 */}
      <Stack mt={2}>
        <Typography variant="sub16Bold">職歴</Typography>
      </Stack>
      <Stack mb={3}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={fieldIds} strategy={verticalListSortingStrategy}>
            {fields.map((field, index) => (
              <SortableWorkExperienceItem
                key={`${field.id}-${renderKey}`}
                fieldId={field.id}
                index={index}
                totalItems={fields.length}
                control={control}
                nationalityOptions={sortedNationalityOptions}
                onRemove={handleRemoveField}
                onAdd={handleInsertField}
                canAdd={fields.length < 10}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Stack>

      {/* 8. 特性メモ — drop-in component, binds to `memo` field. */}
      <TalentFormMemo bordered={false} />

      {/* 9. 面談評価（メモより）— prefilled from upload-step memo via setValue in init effect. */}
      <Stack mt={2}>
        <Typography variant="sub16Bold">面談評価（メモより）</Typography>
        <Grid container spacing={3} mt={0}>
          <Grid item xs={12}>
            <FormField control={control} name="interviewEvaluation" noLabel currentValueType="multiline">
              <Textarea placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      {/* 10. 家族構成 */}
      <Stack mt={2}>
        <Typography variant="sub16Bold">家族構成</Typography>
        <Grid container spacing={3} mt={0}>
          <Grid item xs={12}>
            <FormField control={control} name="familyInformation" noLabel currentValueType="multiline">
              <Textarea placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setDeleteIndex(null);
        }}
        onOk={confirmDelete}
        message="「職歴」を削除してもよろしいですか？"
      />
    </Form>
  );
};
